import { useObjectMemo } from "@fatbees/ui-utils";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode } from "react";
import { CollectionsContext } from "./CollectionsContext";
import { useDevicesCollectionInstance } from "./useDevicesCollectionInstance";
import { useEventsCollectionInstance } from "./useEventsCollectionInstance";
import { useHiveAssignmentsCollectionInstance } from "./useHiveAssignmentsCollectionInstance";
import { useHivesCollectionInstance } from "./useHivesCollectionInstance";
import { useLoadsCollectionInstance } from "./useLoadsCollectionInstance";
import { useScalesCollectionInstance } from "./useScalesCollectionInstance";
import { useUpdateLoadLocations } from "./useUpdateLoadLocations";
import { useUpdateHiveLocations } from "./useUpdateHiveLocations";

export interface CollectionsProviderProps {
  children?: ReactNode;
}

export function CollectionsProvider({ children }: CollectionsProviderProps) {
  const queryClient = useQueryClient();
  const devices = useDevicesCollectionInstance(queryClient);
  const events = useEventsCollectionInstance(queryClient);
  const hiveAssignments = useHiveAssignmentsCollectionInstance(queryClient);
  const hives = useHivesCollectionInstance(queryClient);
  const loads = useLoadsCollectionInstance(queryClient);
  const scales = useScalesCollectionInstance(queryClient);
  const collections = useObjectMemo({
    devices,
    events,
    hiveAssignments,
    hives,
    loads,
    scales,
  });

  useUpdateHiveLocations(events, hives);
  useUpdateLoadLocations(events, loads);

  return (
    <CollectionsContext.Provider value={collections}>
      {children}
    </CollectionsContext.Provider>
  );
}

if (!crypto.randomUUID) {
  // TanStack DB relies on crypto.randomUUID to generate mutation IDs but it's
  // not currently available on FF Mobile
  crypto.randomUUID = () =>
    String(Math.random()) as ReturnType<(typeof crypto)["randomUUID"]>;
}
